"use client";

import { UserContextType, UserInfo } from "@/types/user";
import {
  createContext,
  useContext,
  useState,
  useEffect,
  FC,
  ReactNode,
  useCallback,
} from "react";

// Create context with default values
const UserContext = createContext<UserContextType | undefined>(undefined);

// UserProvider component
interface UserProviderProps {
  children: ReactNode;
  initialUserInfo: UserInfo | null;
}

export const UserProvider: FC<UserProviderProps> = ({
  children,
  initialUserInfo,
}) => {
  const [userInfo, setUser] = useState<UserInfo | null>(initialUserInfo);
  const [loading, setLoading] = useState(false);

  const updateUserInfo = useCallback(
    (newInfo: Partial<NonNullable<UserInfo>>) => {
      setUser((prev) => (prev ? { ...prev, ...newInfo } : prev));
    },
    []
  );

  const setUserInfo = useCallback((newInfo: UserInfo | null) => {
    setUser(newInfo);
  }, []);

  // Client-side fetch as fallback or for updates
  useEffect(() => {
    if (!initialUserInfo) return;

    setLoading(true);
    updateUserInfo(initialUserInfo);
    setLoading(false);
  }, [initialUserInfo]);

  const value: UserContextType = {
    userInfo,
    setUserInfo,
    loading,
    // error,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

// Custom hook to use UserContext
export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};
